import { useEffect, useRef, useState } from "react";

/**
 * Reveal — wraps children with an opacity/transform reveal triggered by
 * IntersectionObserver. Pure CSS animation; React only flips a class.
 *
 * Props:
 *   variant   : 'up' (default) | 'fade' | 'scale' | 'left' | 'right'
 *   delay     : ms delay applied via inline transitionDelay (for stagger)
 *   threshold : 0..1 fraction of element that must be visible (default 0.15)
 *   as        : element tag, default 'div'
 *   className : extra classes
 *   once      : default true — unobserve after first reveal
 */
export default function Reveal({
  children,
  variant = "up",
  delay = 0,
  threshold = 0.15,
  as: Tag = "div",
  className = "",
  once = true,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    if (typeof window !== "undefined" && window.matchMedia) {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) {
        setShown(true);
        return;
      }
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setShown(false);
          }
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, once]);

  const variantClass =
    variant === "fade"
      ? "reveal reveal-fade"
      : variant === "scale"
      ? "reveal reveal-scale"
      : variant === "left"
      ? "reveal reveal-left"
      : variant === "right"
      ? "reveal reveal-right"
      : "reveal";

  return (
    <Tag
      ref={ref}
      className={`${variantClass} ${shown ? "in-view" : ""} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}
